import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getProperties, createProperty, deleteProperty, Property } from '../lib/api';
import { 
  Plus, 
  Buildings,
  Trash,
  MapPin
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';

export default function PropertiesPage() {
  const { t } = useTranslation();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    property_designation: '',
    description: ''
  });

  const fetchProperties = useCallback(async () => {
    try {
      const { data } = await getProperties();
      setProperties(data);
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const handleCreate = async () => {
    if (!formData.name || !formData.address) {
      toast.error('Fyll i namn och adress');
      return;
    }
    
    try {
      await createProperty(formData);
      toast.success(t('saved_successfully'));
      setDialogOpen(false);
      setFormData({ name: '', address: '', property_designation: '', description: '' });
      fetchProperties();
    } catch (error) {
      toast.error(t('error_occurred'));
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    if (!window.confirm('Är du säker?')) return;
    
    try {
      await deleteProperty(id);
      toast.success('Fastigheten har raderats');
      fetchProperties();
    } catch (error) {
      toast.error(t('error_occurred'));
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="properties-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          {t('properties')}
        </h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-press" data-testid="add-property-button">
              <Plus size={18} className="mr-2" />
              {t('add')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Lägg till fastighet</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{t('property_name')} *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="BRF Solsidan"
                  data-testid="property-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('address')} *</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Storgatan 1, 111 22 Stockholm"
                  data-testid="property-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('property_designation')}</Label>
                <Input
                  value={formData.property_designation}
                  onChange={(e) => setFormData(prev => ({ ...prev, property_designation: e.target.value }))}
                  placeholder="STOCKHOLM 1:234"
                  className="font-mono"
                  data-testid="property-designation-input"
                />
              </div>
              <Button onClick={handleCreate} className="w-full" data-testid="save-property-button">
                {t('save')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="card-flat animate-pulse">
              <div className="h-5 w-48 bg-slate-200 rounded mb-2" />
              <div className="h-4 w-64 bg-slate-200 rounded" />
            </div>
          ))}
        </div>
      ) : properties.length === 0 ? (
        <div className="card-flat">
          <div className="empty-state">
            <Buildings size={48} className="empty-state-icon" />
            <p className="text-slate-500">Inga fastigheter ännu</p>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {properties.map((prop) => (
            <div
              key={prop.id}
              className="card-flat group"
              data-testid={`property-card-${prop.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center">
                    <Buildings size={20} className="text-slate-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-slate-900">
                      {prop.name}
                    </h3>
                    <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                      <MapPin size={12} />
                      {prop.address}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(prop.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-500 transition-opacity"
                  data-testid={`delete-property-${prop.id}`}
                >
                  <Trash size={16} />
                </button>
              </div>
              
              {prop.property_designation && (
                <div className="mt-3 pt-3 border-t border-border">
                  <span className="text-xs text-slate-500">{t('property_designation')}:</span>
                  <span className="ml-2 text-xs font-mono text-slate-700">
                    {prop.property_designation}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
